import moment from 'moment';
import { isDateOutOfRange, formatOutOfRangeMessage } from '../../utils/broker-verification-utils';
import {
  BOUNDS_KEY_MAP,
  EMPTY_OUTLIER_RANGES,
  DATE_REFERENCE_LABELS,
  FIELD_KEY_IS_FULLY_EXECUTED_LEASE,
  FIELD_KEY_FULLY_EXECUTED_LEASE,
  FIELD_KEY_HAS_COMMISSION_AGREEMENT,
  FIELD_KEY_COMMISSION_AGREEMENT,
  MULTI_FIELD_BOUNDS_KEY_MAP,
  SQUARE_FOOTAGE_FIELD_KEYS,
} from './voucher-verify-constants';

export const buildOutlierRangesFromBounds = (boundsData) => {
  const ranges = {};
  if (!boundsData) return ranges;
  const base = boundsData.baseRent;
  if (base?.min != null && base?.max != null) {
    ranges.base_rent = { min: parseFloat(base.min), max: parseFloat(base.max) };
  }
  const fieldBounds = boundsData.fieldBounds || {};
  Object.entries(BOUNDS_KEY_MAP).forEach(([apiKey, internalKey]) => {
    const b = fieldBounds[apiKey];
    if (b?.category === 'NUMERIC' && b.min != null && b.max != null) {
      ranges[internalKey] = { min: parseFloat(b.min), max: parseFloat(b.max) };
    } else if (b?.category === 'DATE' && (b.minDate != null || b.maxDate != null || b.minDateReference != null)) {
      ranges[internalKey] = {
        category: 'DATE',
        minDate: b.minDate ?? null,
        maxDate: b.maxDate ?? null,
        ...(b.minDateReference ? { minDateReference: { ...b.minDateReference } } : {}),
      };
    }
  });
  Object.entries(MULTI_FIELD_BOUNDS_KEY_MAP).forEach(([apiKey, internalKeys]) => {
    const b = fieldBounds[apiKey];
    if (b?.category !== 'NUMERIC' || b.min == null || b.max == null) return;
    internalKeys.forEach((internalKey) => {
      ranges[internalKey] = { min: parseFloat(b.min), max: parseFloat(b.max) };
    });
  });
  return ranges;
};

// Resolves a DATE range's `minDateReference` (e.g. "occupancy_date must not be before
// start_date") to an actual value/label -- the bounds API only sends the attributeKey/
// operator since it has no per-lease context. Mirrors PropertyReview.js's own
// `resolveMinDateReference`, sourcing the referenced field's live value from whichever
// is currently known: the in-progress edited value if it's visible/editable on this page,
// else the always-sent `dateReferenceValues` snapshot (covers hidden/is_included:false fields).
export const resolveDateReferenceRanges = (outlierRanges, fieldValues = {}, dateReferenceValues = {}) => {
  let resolved = outlierRanges;
  Object.entries(outlierRanges || {}).forEach(([key, range]) => {
    const ref = range?.minDateReference;
    if (!ref || ref.value) return;

    const refKey = BOUNDS_KEY_MAP[ref.attributeKey];
    const refValue = fieldValues[refKey] !== undefined ? fieldValues[refKey] : dateReferenceValues[refKey];
    if (!refValue) return;

    if (resolved === outlierRanges) resolved = { ...outlierRanges };
    resolved[key] = {
      ...range,
      minDateReference: { ...ref, value: refValue, label: DATE_REFERENCE_LABELS[ref.attributeKey] || ref.attributeKey },
    };
  });
  return resolved;
};

export const formatDateTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const dateText = date.toLocaleDateString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
  });
  const timeText = date.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  });
  return `${dateText} at ${timeText}`;
};

// Same formula as VoucherVerificationTab.js's own getLeaseDuration -- kept in sync so
// Term always matches whatever the config-builder tab and Comp Data section compute.
const AVG_DAYS_PER_MONTH = 365 / 12;
export const getLeaseDuration = (leaseStartDate, leaseEndDate) => {
  if (!leaseStartDate || !leaseEndDate) return null;
  const leaseStart = moment(leaseStartDate);
  const leaseEnd = moment(leaseEndDate);
  const leaseTermDays = leaseEnd.diff(leaseStart, 'days');
  if (leaseTermDays < 0) return null;
  return Math.round(leaseTermDays / AVG_DAYS_PER_MONTH);
};

export const formatAttachmentDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getAttachmentName = attachment => (
  attachment.document_name || attachment.filename || attachment.title || `Attachment ${attachment.id}`
);

export const getAttachmentExtension = (attachment) => {
  const filename = getAttachmentName(attachment);
  const match = filename.match(/\.([^.]+)$/);
  return match ? match[1].toUpperCase() : 'FILE';
};

export const getAttachmentExtensionClass = (attachment) => {
  const ext = getAttachmentExtension(attachment);
  return `doc-type-${ext.toLowerCase()}`;
};

export const normalizeRecipient = (recipient = {}) => {
  if (typeof recipient === 'string') {
    return { email: recipient, work_email: recipient };
  }
  return recipient;
};

export const normalizeNotifiedRecipient = (recipient = {}) => {
  const normalized = normalizeRecipient(recipient);
  const email = normalized.email || normalized.work_email || '';

  if (normalized.first_name || normalized.last_name) {
    return normalized;
  }

  const fullName = (normalized.name || '').trim();
  if (fullName) {
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    return {
      ...normalized,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' '),
    };
  }

  const localPart = email.split('@')[0] || '';
  return {
    ...normalized,
    first_name: localPart || email || 'User',
    last_name: '',
  };
};

export const normalizeField = (field) => {
  const key = field.key || field.field_name;
  const rawValue = field.current_value !== undefined ? field.current_value : field.field_value;
  let currentValue = rawValue;
  if (key === 'rent_escalation_is_custom' && typeof rawValue === 'string' && /^[{[]/.test(rawValue.trim())) {
    try {
      currentValue = JSON.parse(rawValue);
    } catch (error) {
      currentValue = rawValue;
    }
  }
  const labelBase = (field.label || field.field_label || (key === 'lease_term_months' ? 'Term' : key))
    .replace(/ Y\/N$/i, '');
  const isRequiredQuestion = Boolean(field.is_required_question)
    || key === FIELD_KEY_IS_FULLY_EXECUTED_LEASE
    || key === FIELD_KEY_FULLY_EXECUTED_LEASE
    || key === FIELD_KEY_HAS_COMMISSION_AGREEMENT
    || key === FIELD_KEY_COMMISSION_AGREEMENT;
  const label = isRequiredQuestion && !String(labelBase).trim().startsWith('*')
    ? `*${labelBase}`
    : labelBase;

  const outlierMin = field.outlier_min !== null && field.outlier_min !== undefined
    ? parseFloat(field.outlier_min)
    : null;
  const outlierMax = field.outlier_max !== null && field.outlier_max !== undefined
    ? parseFloat(field.outlier_max)
    : null;

  return {
    key,
    label,
    fieldType: field.field_type,
    isEditable: field.is_editable_on_verify ?? field.is_editable ?? false,
    isLocked: field.is_locked ?? !(field.is_editable_on_verify ?? field.is_editable ?? false),
    currentValue,
    escalationValues: field.escalation_values || null,
    isRequiredQuestion,
    isOutlier: Boolean(field.is_outlier),
    outlierLabel: field.outlier_label || null,
    outlierMessage: field.outlier_message || (field.is_outlier ? 'These values are considered outliers. Please validate' : null),
    outlierMin,
    outlierMax,
  };
};

export const isEmptyVerificationValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'number') return Number.isNaN(value);
  if (Array.isArray(value)) {
    return value.length === 0 || value.some(row => (
      row && typeof row === 'object' && (
        ((row.rent_dollar == null || row.rent_dollar === '')
          && (row.rent_percent == null || row.rent_percent === ''))
        || row.months == null || row.months === ''
      )
    ));
  }
  if (typeof value === 'object') {
    if ('rent_escalation_is_percent' in value) {
      const isPercent = value.rent_escalation_is_percent === true
        || String(value.rent_escalation_is_percent).toLowerCase() === 'true';
      const activeValue = isPercent
        ? value.rent_escalation_percent
        : value.rent_escalation_dollar;
      return activeValue === null || activeValue === undefined || String(activeValue).trim() === '';
    }
    return Object.keys(value).length === 0;
  }

  return String(value).trim() === '';
};

export const getRequiredFieldErrorMessage = (label) => {
  const cleanLabel = String(label || '').replace(/^\*\s*/, '').trim();
  return `${cleanLabel || 'This field'} is required`;
};

export const checkFieldOutlier = (field, displayValue, outlierRanges = EMPTY_OUTLIER_RANGES) => {
  const isLeaseEscalation = field.key === 'rent_escalation_is_custom' || field.key === 'lease_escalation';
  const isPercent = displayValue?.rent_escalation_is_percent === true
    || String(displayValue?.rent_escalation_is_percent).toLowerCase() === 'true';
  if (isLeaseEscalation && Array.isArray(displayValue)) return false;
  const rangeKey = isLeaseEscalation
    ? (isPercent ? 'rent_escalation_percent' : 'rent_escalation_dollar')
    : field.key;
  const range = outlierRanges[rangeKey];

  if (field.fieldType === 'date' || range?.category === 'DATE') {
    if (!range) return field.isOutlier;
    return isDateOutOfRange(displayValue, range);
  }

  const min = range?.min ?? field.outlierMin;
  const max = range?.max ?? field.outlierMax;

  if (min === null || min === undefined || max === null || max === undefined) {
    return field.isOutlier;
  }

  if (field.fieldType === 'free_rent') {
    const totalMonths = ((displayValue?.years || 0) * 12) + (displayValue?.months || 0);
    return totalMonths < min || totalMonths > max;
  }

  const rawValue = isLeaseEscalation && displayValue && typeof displayValue === 'object'
    ? displayValue[isPercent ? 'rent_escalation_percent' : 'rent_escalation_dollar']
    : displayValue;
  const stringVal = String(rawValue ?? '').trim();
  if (stringVal === '') return false;

  const num = parseFloat(stringVal);
  if (Number.isNaN(num)) return false;

  return num < min || num > max;
};

export const formatRangeNumber = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(2) : String(n);
};

export const getOutlierRangeLabel = (fieldKey, fieldType, range) => {
  if (!range) return null;

  if (fieldType === 'date' || range.category === 'DATE') {
    return formatOutOfRangeMessage(range, 'date') || null;
  }

  if (SQUARE_FOOTAGE_FIELD_KEYS.includes(fieldKey)) {
    const formatSqft = n => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-US') : String(n));
    return `Typical range: ${formatSqft(range.min)}-${formatSqft(range.max)} SF`;
  }

  const min = formatRangeNumber(range.min);
  const max = formatRangeNumber(range.max);

  if (fieldType === 'free_rent') return `Typical range: ${min}-${max} months`;
  if (fieldKey === 'base_rent') return `Typical range: ${min}-${max}/sqft per year`;
  if (fieldKey === 'tenant_improvement_allowance_amount_per_sqft') return `Typical range: ${min}-${max}/SF`;
  if (fieldKey === 'operating_expenses_amount_per_sqft') return `Typical range: ${min}-${max}/SF`;
  if (fieldKey === 'rent_escalation_percent') return `Typical range: ${min}-${max}%`;
  if (fieldKey === 'lease_term_months') return `Typical range: ${min}-${max} months`;

  return `Typical range: ${min}-${max}`;
};
